import { computed, type Ref } from 'vue';

/**
 * Generic lookup primitive for a client-side data dictionary. Indexes a list of
 * entries by their numeric `id` and resolves reactively — when the underlying
 * dictionary loads or refreshes, consumers re-run automatically.
 *
 * This is the shared core behind the per-domain lookup composables
 * (`useUnitLookup`, `useRegionLookup`, …). It should not be used directly by
 * components; wrap it in a domain composable that binds the relevant store's
 * dictionary and returns a typed accessor.
 */
export function useDataDictionary<TEntry extends { id: number }>(
  entries: Ref<TEntry[]>
) {
  const byId = computed(
    () => new Map(entries.value.map((entry) => [entry.id, entry]))
  );

  const get = (id: number | string | null | undefined): TEntry | null => {
    if (id == null) return null;
    return byId.value.get(Number(id)) ?? null;
  };

  return { get, byId };
}
